import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import './MobileBreakpoints.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

// appendPx wird jetzt aus LayoutUtils importiert

function HolisticTestimonial({ data, onRemove, isSelected, isEditMode, onUpdate, onSelectWidget }) {
    const deleteLogo = '/img/delete.svg';
    const fonts = useFont();
    
    // INLINE-EDITING SYSTEM
    const [isInlineEditing, setIsInlineEditing] = useState(null);
    const [inlineValue, setInlineValue] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    
    // Mobile Layout Detection
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const containerRef = useRef(null);
    
    // Ref für Outside-Click Detection
    const widgetRef = useRef(null);

    // Delete Handler
    const handleDelete = (event) => {
        event.stopPropagation();
        if (onRemove) {
            onRemove({ stopPropagation: () => {} }, data.id);
        }
        setShowDeleteModal(false);
    };

    // Mobile Breakpoint Detection (750px)
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Helper functions (consistent with other widgets)
    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    // INLINE-EDITING FUNCTIONS
    const handleInlineEdit = (fieldName, currentValue) => {
        if (!isEditMode || !onUpdate) return;
        
        // Widget selektieren wenn nicht bereits selektiert
        if (!isSelected && onSelectWidget) {
            onSelectWidget(data.id);
        }
        
        if (isInlineEditing === fieldName) {
            handleInlineCancel();
            return;
        }
        setIsInlineEditing(fieldName);
        setInlineValue(currentValue || '');
    };

    const handleInlineSubmit = useCallback((fieldName) => {
        if (!onUpdate) {
            console.warn('⚠️ onUpdate not available');
            return;
        }
        
        console.log('💾 HolisticTestimonial Saving field:', fieldName, 'with value:', inlineValue);
        const updatedData = { ...data, [fieldName]: inlineValue };
        onUpdate(updatedData);
        setIsInlineEditing(null);
        setInlineValue('');
        console.log('✅ HolisticTestimonial Save completed for:', fieldName);
    }, [onUpdate, data, inlineValue]);

    const handleInlineCancel = () => {
        setIsInlineEditing(null);
        setInlineValue('');
    };

    // Smart Outside-Click Handler
    useEffect(() => {
        if (!isInlineEditing) return;

        const handleOutsideClick = (event) => {
            const isEditingInput = event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA';
            const isActionButton = event.target.hasAttribute('data-action-button') || event.target.closest('[data-action-button]');
            const isDeleteButton = event.target.closest('.delete_button');
            const isWidgetEdit = event.target.closest('.widget-edit-container');
            const isWithinWidget = widgetRef.current?.contains(event.target);
            
            const isClickOnOtherTextElement = event.target.tagName === 'P' || 
                                            event.target.tagName === 'H1' || 
                                            event.target.tagName === 'H2' || 
                                            event.target.tagName === 'H3' || 
                                            event.target.tagName === 'DIV';
            
            const isRealOutsideClick = (!isEditingInput && !isActionButton && !isDeleteButton && !isWidgetEdit && !isWithinWidget) ||
                                      (isClickOnOtherTextElement && isWithinWidget && !isEditingInput);

            if (isRealOutsideClick && isInlineEditing) {
                handleInlineSubmit(isInlineEditing);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick, true);
        return () => document.removeEventListener('mousedown', handleOutsideClick, true);
    }, [isInlineEditing, inlineValue, data, onUpdate, handleInlineSubmit]);

    const handleInlineKeyPress = (e, fieldName) => {
        // Dynamische Detection: Ist es gerade ein Textarea?
        const isCurrentlyTextarea = e.target.tagName === 'TEXTAREA';
        
        if (e.key === 'Enter') {
            if (isCurrentlyTextarea) {
                // Textareas: Enter = neue Zeile, Ctrl+Enter = speichern
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    handleInlineSubmit(fieldName);
                }
                // Enter allein = neue Zeile (normales Verhalten)
            } else {
                // Input-Felder: Enter = speichern
                e.preventDefault();
                handleInlineSubmit(fieldName);
            }
        } else if (e.key === 'Escape') {
            handleInlineCancel();
        }
    };

    // Render Editable Text (Enhanced like SimplisticTestimonial)
    const renderEditableText = (fieldName, currentValue, Element = 'h2', style = {}, placeholder = 'Click to edit...') => {
        if (!isEditMode) {
            return currentValue ? (
                <Element style={{
                    ...style,
                    whiteSpace: style.whiteSpace || 'pre-wrap'
                }}>
                    {currentValue}
                </Element>
            ) : null;
        }

        if (isInlineEditing === fieldName) {
            // Action Buttons Container
            const renderActionButtons = () => (
                <div style={{
                    position: 'absolute',
                    top: '-11px',
                    right: '-6px',
                    display: 'flex',
                    gap: '4px',
                    zIndex: 9999,
                    background: 'rgba(255, 255, 255, 0.95)',
                    padding: '2px 4px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(4px)',
                    border: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    <button
                        data-action-button="save"
                        onClick={() => handleInlineSubmit(fieldName)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease'
                        }}
                        title="Save"
                    >
                        ✓
                    </button>
                    
                    <button
                        data-action-button="cancel"
                        onClick={handleInlineCancel}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            padding: '0',
                            fontSize: '9px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '16px',
                            height: '16px',
                            transition: 'all 0.2s ease'
                        }}
                        title="Cancel"
                    >
                        ×
                    </button>
                </div>
            );

            return (
                <div style={{ position: 'relative', width: '100%' }}>
                    <input
                        type="text"
                        value={inlineValue}
                        onChange={(e) => setInlineValue(e.target.value)}
                        onKeyDown={(e) => handleInlineKeyPress(e, fieldName)}
                        placeholder={placeholder}
                        autoFocus
                        style={{
                            ...style,
                            border: 'none',
                            borderRadius: '0',
                            padding: '0',
                            background: 'transparent',
                            outline: 'none',
                            width: '100%',
                            fontFamily: style.fontFamily,
                            fontSize: style.fontSize,
                            fontWeight: style.fontWeight,
                            color: style.color
                        }}
                    />
                    {renderActionButtons()}
                </div>
            );
        }

        return (
            <Element 
                style={{ 
                    ...style, 
                    cursor: 'pointer',
                    minHeight: '1.2em',
                    border: '2px solid transparent',
                    borderRadius: '4px',
                    padding: '8px',
                    transition: 'all 0.2s ease'
                }}
                onClick={() => handleInlineEdit(fieldName, currentValue)}
                title="Click to edit"
                onMouseEnter={(e) => {
                    if (isEditMode) {
                        e.target.style.backgroundColor = 'rgba(102, 126, 234, 0.05)';
                        e.target.style.borderColor = 'rgba(102, 126, 234, 0.2)';
                    }
                }}
                onMouseLeave={(e) => {
                    if (isEditMode) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = 'transparent';
                    }
                }}
            >
                {currentValue || placeholder}
            </Element>
        );
    };

    // ✅ RESPONSIVE STARS RENDERING - NEW LAYOUT SYSTEM 2025
    const renderStars = (rating) => {
        if (!data.showStars) return null;
        
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <span 
                    key={i} 
                    style={{ 
                        color: i <= rating ? (data.starColor || '#ffd700') : '#e0e0e0',
                        fontSize: appendPx(getResponsiveValue(data, 'starSize', isMobileLayout, 14)),
                        marginRight: appendPx(getResponsiveValue(data, 'starSpacing', isMobileLayout, 2)),
                        // ✅ RESPONSIVE CSS PROPERTIES
                        '--mobile-size': appendPx(data.starSizeMobile || data.starSize || 14),
                        '--normal-size': appendPx(data.starSize || 14),
                        '--big-size': appendPx(data.starSizeBig || data.starSize || 14)
                    }}
                    className="responsive-font-size"
                >
                    ★
                </span>
            );
        }
        return <div className="testimonial-stars" style={{ marginBottom: '12px' }}>{stars}</div>;
    };

    // 🚀 RESPONSIVE LAYOUT STYLES - NEW LAYOUT SYSTEM 2025
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor,
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none',
        cursor: isEditMode ? 'pointer' : 'default'
    });

    // ✅ EINHEITLICHE RESPONSIVE HEIGHT LOGIK - wie HeroHeader
    const widgetStyle = {
        ...layoutStyles,
        // ✅ FALLBACK für alte Height-Properties (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(isMobileLayout ? 
                (data.heightMobile || 1200) : // Mobile: Template-Default 1200px
                (data.height || 600)         // Desktop: Template-Default 600px
            )
        })
    };

    // ✅ RESPONSIVE TITLE STYLES - NEW LAYOUT SYSTEM 2025
    const titleStyle = {
        ...((!data.titleEffect || data.titleEffect === 'none') && {
            color: data.titleColor || '#2c3e50'
        }),
        fontSize: appendPx(data.titleSize || 32),
        fontWeight: data.titleWeight || '600',
        fontFamily: data.titleFont || fonts.globalBigFont,
        lineHeight: data.titleLineHeight || '1.3',
        marginBottom: appendPx(data.titleMarginBottom || 30),
        textAlign: isMobileLayout ? 
            (data.textAlignmentMobile || data.textAlignment || 'center') : 
            (data.textAlignment || 'left'),
        margin: 0,
        ...getTextEffectStyles(data.titleEffect),
        // ✅ RESPONSIVE CSS PROPERTIES
        '--mobile-size': appendPx(data.titleSizeMobile || data.titleSize || 32),
        '--normal-size': appendPx(data.titleSize || 32),
        '--big-size': appendPx(data.titleSizeBig || data.titleSize || 32)
    };

    // ✅ RESPONSIVE CONTAINER STYLES - NEW LAYOUT SYSTEM 2025
    const containerStyle = {
        maxWidth: data.layoutType === 'linear-list' ? '100%' : appendPx(data.contentMaxWidth || 1200),
        margin: data.layoutType === 'linear-list' ? '0' : '0 auto',
        width: '100%',
        // ✅ RESPONSIVE PADDING mit getResponsiveValue
        padding: data.layoutType === 'linear-list' 
            ? `0 ${getResponsiveValue(data, 'paddingLeft', isMobileLayout, 20)}px`
            : `0 ${getResponsiveValue(data, 'paddingLeft', isMobileLayout, 0)}px`
    };

    const getLayoutStyle = () => {
        if (data.layoutType === 'linear-list') {
            return {
                display: 'flex',
                flexDirection: 'column',
                gap: '0px'
            };
        }
        
        // Grid layout (default) - mobile responsive
        const columnsPerRow = isMobileLayout 
            ? 1  // Force single column on mobile for better readability
            : data.cardsPerRow;
            
        const gridGap = isMobileLayout 
            ? (data.mobileCardSpacing || Math.max(16, data.cardSpacing * 0.7))
            : data.cardSpacing;
        
        return {
            display: 'grid',
            gridTemplateColumns: `repeat(${columnsPerRow}, 1fr)`,
            gap: gridGap > 0 ? appendPx(gridGap) : '0px',
            border: data.cardBorder && gridGap === 0 ? `${data.cardBorderWidth}px solid ${data.cardBorderColor}` : 'none'
        };
    };

    const cardsGridStyle = getLayoutStyle();

    const cardStyle = (index) => {
        const basePadding = isMobileLayout 
            ? Math.max(16, data.cardPadding * 0.8)
            : data.cardPadding;
            
        const baseStyle = {
            backgroundColor: data.cardBackgroundColor,
            borderRadius: appendPx(data.cardBorderRadius),
            padding: appendPx(basePadding),
            boxShadow: data.cardShadow ? `0 4px 12px ${data.cardShadowColor}` : 'none',
            transition: data.enableCardAnimations ? 'all 0.3s ease' : 'none',
            cursor: isEditMode ? 'default' : 'auto',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        };

        // Linear list styling (like in the image)
        if (data.layoutType === 'linear-list') {
            return {
                ...baseStyle,
                border: 'none',
                borderTop: index === 0 ? `${data.dividerWidth || 1}px solid ${data.dividerColor || '#e8e8e8'}` : 'none', // Top border for first item
                borderBottom: `${data.dividerWidth || 1}px solid ${data.dividerColor || '#e8e8e8'}`,
                borderRadius: 0, // No border radius for seamless list
                paddingTop: `${data.cardPadding}px`,
                paddingBottom: `${data.cardPadding}px`,
                paddingLeft: `${data.cardPadding}px`,
                paddingRight: `${data.cardPadding}px` // Explicit padding on all sides
            };
        }
        
        // Seamless grid styling
        if (data.cardSpacing === 0 && data.cardBorder) {
            return {
                ...baseStyle,
                border: 'none',
                borderRight: index < data.cardsPerRow - 1 ? `${data.cardBorderWidth}px solid ${data.cardBorderColor}` : 'none',
                borderBottom: `${data.cardBorderWidth}px solid ${data.cardBorderColor}`
            };
        }

        // Standard card styling
        return {
            ...baseStyle,
            border: data.cardBorder ? `${data.cardBorderWidth}px solid ${data.cardBorderColor}` : 'none'
        };
    };

    // ✅ RESPONSIVE TESTIMONIAL TEXT STYLES - NEW LAYOUT SYSTEM 2025
    const testimonialTextStyle = {
        ...((!data.testimonialTextEffect || data.testimonialTextEffect === 'none') && {
            color: data.testimonialTextColor || '#4a5568'
        }),
        fontSize: appendPx(data.testimonialTextSize || 16),
        fontWeight: data.testimonialTextWeight || '400',
        fontFamily: data.testimonialTextFont || fonts.globalSmallFont,
        lineHeight: data.testimonialTextLineHeight || '1.6',
        marginBottom: isMobileLayout ? '12px' : '16px',
        flex: 1,
        textAlign: isMobileLayout ? 
            (data.testimonialTextAlignmentMobile || 'left') : 
            (data.testimonialTextAlignment || 'left'),
        ...getTextEffectStyles(data.testimonialTextEffect),
        // ✅ RESPONSIVE CSS PROPERTIES
        '--mobile-size': appendPx(data.testimonialTextSizeMobile || data.testimonialTextSize || 16),
        '--normal-size': appendPx(data.testimonialTextSize || 16),
        '--big-size': appendPx(data.testimonialTextSizeBig || data.testimonialTextSize || 16)
    };

    const authorSectionStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginTop: 'auto'
    };

    // ✅ RESPONSIVE AVATAR STYLES - NEW LAYOUT SYSTEM 2025
    const avatarStyle = {
        width: appendPx(getResponsiveValue(data, 'avatarSize', isMobileLayout, 48)),
        height: appendPx(getResponsiveValue(data, 'avatarSize', isMobileLayout, 48)),
        borderRadius: `${data.avatarBorderRadius || 50}%`,
        border: data.avatarBorder ? `${data.avatarBorderWidth || 2}px solid ${data.avatarBorderColor || '#e0e0e0'}` : 'none',
        objectFit: 'cover',
        // ✅ RESPONSIVE CSS PROPERTIES
        '--mobile-size': appendPx(data.avatarSizeMobile || data.avatarSize || 48),
        '--normal-size': appendPx(data.avatarSize || 48),
        '--big-size': appendPx(data.avatarSizeBig || data.avatarSize || 48)
    };

    const authorInfoStyle = {
        display: 'flex',
        flexDirection: 'column'
    };

    // ✅ RESPONSIVE AUTHOR STYLES - NEW LAYOUT SYSTEM 2025
    const authorNameStyle = {
        color: data.authorNameColor || '#2c3e50',
        fontSize: appendPx(data.authorNameSize || 14),
        fontWeight: data.authorNameWeight || '600',
        fontFamily: data.authorNameFont || fonts.globalSmallFont,
        margin: 0,
        lineHeight: 1.2,
        // ✅ RESPONSIVE CSS PROPERTIES
        '--mobile-size': appendPx(data.authorNameSizeMobile || data.authorNameSize || 14),
        '--normal-size': appendPx(data.authorNameSize || 14),
        '--big-size': appendPx(data.authorNameSizeBig || data.authorNameSize || 14)
    };

    const authorLocationStyle = {
        color: data.authorLocationColor || '#666666',
        fontSize: appendPx(data.authorLocationSize || 12),
        fontWeight: data.authorLocationWeight || '400',
        fontFamily: data.authorLocationFont || fonts.globalSmallFont,
        margin: 0,
        lineHeight: 1.2,
        // ✅ RESPONSIVE CSS PROPERTIES
        '--mobile-size': appendPx(data.authorLocationSizeMobile || data.authorLocationSize || 12),
        '--normal-size': appendPx(data.authorLocationSize || 12),
        '--big-size': appendPx(data.authorLocationSizeBig || data.authorLocationSize || 12)
    };


    // Handle widget click for selection
    const handleWidgetClick = (e) => {
        if (isEditMode && onSelectWidget && !isSelected) {
            e.stopPropagation();
            onSelectWidget(data.id);
        }
    };

    // ✅ SIMPLIFIED RESPONSIVE STYLES - NEW LAYOUT SYSTEM 2025 handles most responsive logic
    const responsiveStyles = `
        /* Grid Layout Responsive */
        @media (max-width: 750px) {
            .holistic-testimonial-grid {
                grid-template-columns: 1fr !important;
            }
        }
        
        @media (max-width: 1024px) and (min-width: 751px) {
            .holistic-testimonial-grid {
                grid-template-columns: repeat(${data.cardsPerRowTablet || 2}, 1fr) !important;
            }
        }
        
        /* Card Hover Effects */
        .holistic-testimonial-card:hover {
            ${data.cardHoverBackground ? `background-color: ${data.cardHoverBackground} !important;` : ''}
            ${data.cardHoverShadow ? `box-shadow: 0 8px 24px ${data.cardHoverShadowColor} !important;` : ''}
            ${data.cardHoverTransform ? `transform: scale(${data.cardHoverScale}) !important;` : ''}
        }
        
        
        /* Seamless Grid Borders (for minimalistic style) */
        ${data.cardSpacing === 0 && data.cardBorder ? `
            .holistic-testimonial-grid {
                border: ${data.cardBorderWidth}px solid ${data.cardBorderColor};
            }
            
            .holistic-testimonial-card {
                border-right: ${data.cardBorderWidth}px solid ${data.cardBorderColor};
                border-bottom: ${data.cardBorderWidth}px solid ${data.cardBorderColor};
            }
            
            .holistic-testimonial-card:nth-child(${data.cardsPerRow}n) {
                border-right: none;
            }
            
            @media (max-width: 1024px) {
                .holistic-testimonial-card:nth-child(${data.cardsPerRowTablet}n) {
                    border-right: none;
                }
                .holistic-testimonial-card:nth-child(n+${data.cardsPerRowTablet + 1}) {
                    border-right: ${data.cardBorderWidth}px solid ${data.cardBorderColor};
                }
            }
            
            @media (max-width: 768px) {
                .holistic-testimonial-card {
                    border-right: none;
                }
            }
        ` : ''}
    `;

    return (
        <>
            <style>{responsiveStyles}</style>
            <div 
                ref={(el) => {
                    containerRef.current = el;
                    widgetRef.current = el;
                }}
                style={widgetStyle}
                onClick={handleWidgetClick}
                className={`widget-container holistic-testimonial ${isSelected ? 'selected' : ''} layout-${data.layoutType || 'linear-list'}`}
            >
                {/* Delete Button */}
                {isEditMode && (
                    <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                        <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                    </button>
                )}
                
                {/* 🤖 AI REFRESH ICON */}
                {isEditMode && (
                    <AIRefreshIcon 
                        widget={{
                            ...data,
                            type: 'HolisticTestimonial'
                        }}
                        spaceId={null}
                        onContentRefreshed={(refreshedWidget) => {
                            console.log('🤖 [HOLISTICTESTIMONIAL] AI-refreshed content received:', refreshedWidget);
                            onUpdate(refreshedWidget);
                        }}
                        position="top-left"
                    />
                )}

                {/* Container */}
                <div style={containerStyle} className="holistic-testimonial-container">
                    {/* Title - with Show/Hide Toggle */}
                    {data.showTitleText !== false && renderEditableText('titleText', data.titleText, 'h2', titleStyle, 'Enter testimonials title...')}

                    {/* Testimonial Content */}
                    <div style={cardsGridStyle} className="holistic-testimonial-grid">
                        {data.testimonials?.map((testimonial, index) => (
                            <div key={testimonial.id}>
                                {data.layoutType === 'linear-list' ? (
                                    /* Linear List Layout (like in image) */
                                    <div
                                        style={{
                                            ...cardStyle(index),
                                            borderBottom: data.showDividers && index < data.testimonials.length - 1 ? 
                                                `${data.dividerWidth}px solid ${data.dividerColor}` : 'none'
                                            // Padding wird jetzt komplett von cardStyle(index) gehandhabt
                                        }}
                                        className="holistic-testimonial-card linear-list-item"
                                        onMouseEnter={(e) => {
                                            if (!isEditMode && data.cardHoverBackground) {
                                                e.currentTarget.style.backgroundColor = data.cardHoverBackground;
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isEditMode && data.cardHoverBackground) {
                                                e.currentTarget.style.backgroundColor = data.cardBackgroundColor;
                                            }
                                        }}
                                    >
                                        {/* Stars */}
                                        {renderStars(testimonial.rating)}

                                        {/* Testimonial Text */}
                                        <div style={{
                                            ...testimonialTextStyle,
                                            marginBottom: '16px'
                                        }} className="holistic-testimonial-text">
                                            {testimonial.text}
                                        </div>

                                        {/* Author Section - Avatar Right */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            {/* Author Info Left */}
                                            <div style={authorInfoStyle}>
                                                <div style={authorNameStyle} className="holistic-testimonial-author-name">
                                                    {testimonial.author}
                                                </div>
                                                <div style={authorLocationStyle} className="holistic-testimonial-author-location">
                                                    {testimonial.location}
                                                </div>
                                            </div>

                                            {/* Avatar Right */}
                                            {data.showAvatars && testimonial.avatar && (
                                                <img
                                                    src={testimonial.avatar}
                                                    alt={testimonial.author}
                                                    style={avatarStyle}
                                                />
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    /* Grid Layout (original) */
                                    <div
                                        style={cardStyle(index)}
                                        className="holistic-testimonial-card"
                                        onMouseEnter={(e) => {
                                            if (!isEditMode && data.cardHoverBackground) {
                                                e.currentTarget.style.backgroundColor = data.cardHoverBackground;
                                            }
                                            if (!isEditMode && data.cardHoverShadow) {
                                                e.currentTarget.style.boxShadow = `0 8px 24px ${data.cardHoverShadowColor}`;
                                            }
                                            if (!isEditMode && data.cardHoverTransform) {
                                                e.currentTarget.style.transform = `scale(${data.cardHoverScale})`;
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!isEditMode && data.cardHoverBackground) {
                                                e.currentTarget.style.backgroundColor = data.cardBackgroundColor;
                                            }
                                            if (!isEditMode && data.cardHoverShadow) {
                                                e.currentTarget.style.boxShadow = data.cardShadow ? `0 4px 12px ${data.cardShadowColor}` : 'none';
                                            }
                                            if (!isEditMode && data.cardHoverTransform) {
                                                e.currentTarget.style.transform = 'scale(1)';
                                            }
                                        }}
                                    >
                                        {/* Stars */}
                                        {renderStars(testimonial.rating)}

                                        {/* Testimonial Text */}
                                        <div style={testimonialTextStyle} className="holistic-testimonial-text">
                                            {testimonial.text}
                                        </div>

                                        {/* Author Section */}
                                        <div style={authorSectionStyle}>
                                            {/* Avatar */}
                                            {data.showAvatars && testimonial.avatar && (
                                                <img
                                                    src={testimonial.avatar}
                                                    alt={testimonial.author}
                                                    style={avatarStyle}
                                                />
                                            )}

                                            {/* Author Info */}
                                            <div style={authorInfoStyle}>
                                                <div style={authorNameStyle} className="holistic-testimonial-author-name">
                                                    {testimonial.author}
                                                </div>
                                                <div style={authorLocationStyle} className="holistic-testimonial-author-location">
                                                    {testimonial.location}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                </div>

                {/* Delete Modal */}
                {isEditMode && showDeleteModal && (
                    <DeleteModal
                        isOpen={showDeleteModal}
                        onClose={() => setShowDeleteModal(false)}
                        onConfirm={handleDelete}
                    />
                )}
            </div>
        </>
    );
}

export default HolisticTestimonial;
